import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { recordingAPI, blockchainAPI } from '../services/api';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import BlockchainLockIcon from '../components/BlockchainLockIcon';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useRecordings } from '../hooks/useRecordings';
import './Recordings.css';

// ─── Audio Player ────────────────────────────────────────────────────────────
// Uses IntersectionObserver (once) → fetches a 10-min stream-scoped JWT →
// sets it as <audio src> query param. Native browser streaming: range requests,
// seek support, zero blob memory pressure.

function AudioPlayer({ recordingId }) {
  const [containerRef, isVisible] = useIntersectionObserver({ rootMargin: '150px', threshold: 0, once: true });
  const [audioSrc, setAudioSrc] = useState(null);
  const [tokenStatus, setTokenStatus] = useState('idle');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    setTokenStatus('fetching');
    setAudioSrc(null);
    const controller = new AbortController();
    recordingAPI
      .getStreamToken(recordingId, controller.signal)
      .then(({ data }) => {
        setAudioSrc(recordingAPI.getStreamUrl(recordingId, data.token));
        setTokenStatus('ready');
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') return;
        setTokenStatus('error');
      });
    return () => controller.abort();
  }, [isVisible, recordingId, retryCount]);

  const retry = () => { setTokenStatus('idle'); setRetryCount(c => c + 1); };

  return (
    <div ref={containerRef} className="rec-player">
      {tokenStatus === 'idle' && <div className="audio-placeholder" />}
      {tokenStatus === 'fetching' && <div className="audio-loading">Loading player…</div>}
      {tokenStatus === 'error' && (
        <button className="audio-load-btn" onClick={retry}>
          <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
            <path d="M2 8a6 6 0 1 1 1.5 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <path d="M2 12V8h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Retry Player
        </button>
      )}
      {tokenStatus === 'ready' && audioSrc && (
        <audio controls src={audioSrc} className="audio-player" onError={retry} />
      )}
    </div>
  );
}

// ─── Skeleton Card ───────────────────────────────────────────────────────────

function RecordingCardSkeleton() {
  return (
    <div className="rec-card rec-card--skeleton">
      <div className="rec-card__top-bar" />
      <div className="rec-card__body">
        <div className="skel skel--title" />
        <div className="skel skel--stats" />
        <div className="skel skel--player" />
        <div className="skel skel--actions" />
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRoomId(code) {
  if (!code) return null;
  const u = code.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return `VM-${u.slice(0, 4)}-${u.slice(4, 8)}`;
}

const fmtDuration = (s) => {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const fmtSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// ─── Recordings Page ─────────────────────────────────────────────────────────

function Recordings() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    recordings,
    isLoading,
    isFetchingMore,
    isError,
    error,
    hasMore,
    loadMore,
    removeRecording,
    patchRecording,
  } = useRecordings();

  // Optimistic UI: local mirror of canonical state.
  // Applied immediately on user action; rolled back on server error.
  const [optimisticRecordings, setOptimisticRecordings] = useState(recordings);
  useEffect(() => { setOptimisticRecordings(recordings); }, [recordings]);

  const [transcribingId, setTranscribingId] = useState(null);
  const [expandedTranscript, setExpandedTranscript] = useState(null);
  const [certStatuses, setCertStatuses] = useState({});
  const fetchedCertIdsRef = useRef(new Set());

  // Infinite scroll sentinel — fires when user scrolls within 300px of the bottom
  const [sentinelRef, isSentinelVisible] = useIntersectionObserver({ rootMargin: '300px', threshold: 0 });

  useEffect(() => {
    if (isSentinelVisible && hasMore && !isFetchingMore) loadMore();
  }, [isSentinelVisible, hasMore, isFetchingMore, loadMore]);

  // Fetch blockchain cert statuses only for newly loaded recording IDs
  useEffect(() => {
    if (user?.plan !== 'pro' || !recordings.length) return;
    const missing = recordings.map((r) => r.id).filter((id) => !fetchedCertIdsRef.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => fetchedCertIdsRef.current.add(id));
    blockchainAPI
      .getStatuses(missing)
      .then((res) => setCertStatuses((prev) => ({ ...prev, ...res.data })))
      .catch(() => {});
  }, [recordings, user?.plan]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async (id) => {
    setOptimisticRecordings((prev) => prev.filter((r) => r.id !== id));
    try {
      await recordingAPI.delete(id);
      removeRecording(id);
      toast.success('Recording deleted');
    } catch {
      setOptimisticRecordings(recordings); // rollback
      toast.error('Failed to delete recording');
    }
  }, [recordings, removeRecording]);

  const handleTranscribe = useCallback(async (id) => {
    setTranscribingId(id);
    try {
      const res = await recordingAPI.transcribe(id);
      const patch = {
        transcript: res.data.transcript,
        transcript_data: res.data.transcript_data,
        transcript_status: 'completed',
      };
      patchRecording(id, patch);
      setExpandedTranscript(id);
      toast.success('Transcription complete!');
    } catch {
      toast.error('Transcription failed. Please try again.');
    } finally {
      setTranscribingId(null);
    }
  }, [patchRecording]);

  const handleDownload = async (id, title) => {
    try {
      const { data: tokenData } = await recordingAPI.getStreamToken(id);
      const url = recordingAPI.getStreamUrl(id, tokenData.token);
      const res = await fetch(url);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${title}_recording.webm`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch {
      toast.error('Failed to download recording');
    }
  };

  const copyTranscript = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Transcript copied!'));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="rec-page">

      <header className="rec-header">
        <div className="rec-header__left">
          <div className="rec-brand-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4" stroke="#fff" strokeWidth="2"/>
              <path d="M6 14a6 6 0 0 1 12 0" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              <rect x="10" y="18" width="4" height="2" rx="1" fill="#fff"/>
            </svg>
          </div>
          <h1 className="rec-header__title">Recordings</h1>
        </div>
        <button className="rec-back-btn" onClick={() => navigate('/dashboard')}>
          <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
            <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
      </header>

      <div className="rec-content">
        {isLoading ? (
          <div className="rec-list">
            {Array.from({ length: 4 }).map((_, i) => <RecordingCardSkeleton key={i} />)}
          </div>
        ) : isError ? (
          <div className="rec-empty">
            <p className="rec-empty__text">Failed to load recordings</p>
            <p className="rec-empty__sub">{error}</p>
          </div>
        ) : optimisticRecordings.length === 0 ? (
          <div className="rec-empty">
            <div className="rec-empty__icon">
              <svg viewBox="0 0 48 48" fill="none" width="48" height="48">
                <circle cx="24" cy="17" r="8" stroke="currentColor" strokeWidth="2.5"/>
                <path d="M12 32a12 12 0 0 1 24 0" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="rec-empty__text">No recordings yet</p>
            <p className="rec-empty__sub">Record your next meeting to see it here</p>
            <button onClick={() => navigate('/dashboard')} className="rec-empty__btn">
              Go to Meetings
            </button>
          </div>
        ) : (
          <>
            <div className="rec-list">
              {optimisticRecordings.map((rec, index) => (
                <motion.div
                  key={rec.id}
                  className="rec-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(index, 5) * 0.07 }}
                >
                  <div className="rec-card__top-bar" />
                  <div className="rec-card__body">

                    <div className="rec-card__title-row">
                      <div className="rec-card__mic-icon">
                        <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
                          <rect x="8" y="2" width="8" height="13" rx="4" stroke="currentColor" strokeWidth="1.8"/>
                          <path d="M5 11a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          <line x1="12" y1="18" x2="12" y2="22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          <line x1="9" y1="22" x2="15" y2="22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <h3 className="rec-card__title">{rec.meeting_title}</h3>
                      <span className="rec-card__date">
                        {new Date(rec.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>

                    <div className="rec-stats">
                      <span className="rec-stat">
                        <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
                          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M8 5v3.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        {fmtDuration(rec.duration)}
                      </span>
                      <span className="rec-stat">
                        <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
                          <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                        </svg>
                        {fmtSize(rec.size)}
                      </span>
                      {rec.room_id && (
                        <span className="rec-stat rec-stat--room">
                          {formatRoomId(rec.room_id)}
                        </span>
                      )}
                    </div>

                    <AudioPlayer recordingId={rec.id} />

                    <div className="rec-actions">
                      <button
                        className="rec-btn rec-btn--download"
                        onClick={() => handleDownload(rec.id, rec.meeting_title)}
                      >
                        <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
                          <path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 12h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                        Download
                      </button>

                      {rec.transcript ? (
                        <button
                          className="rec-btn rec-btn--transcript"
                          onClick={() => setExpandedTranscript(expandedTranscript === rec.id ? null : rec.id)}
                        >
                          <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
                            <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M5 6h6M5 9h4M5 12h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                          </svg>
                          {expandedTranscript === rec.id ? 'Hide' : 'Transcript'}
                        </button>
                      ) : (
                        <button
                          className="rec-btn rec-btn--transcribe"
                          onClick={() => handleTranscribe(rec.id)}
                          disabled={transcribingId === rec.id}
                        >
                          {transcribingId === rec.id
                            ? <><span className="btn-spinner" />Transcribing…</>
                            : <>
                                <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
                                  <rect x="5" y="1.5" width="6" height="9" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                                  <path d="M3 8a5 5 0 0 0 10 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                  <line x1="8" y1="13" x2="8" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                                Transcribe
                              </>
                          }
                        </button>
                      )}

                      <button
                        className="rec-btn rec-btn--delete"
                        onClick={() => handleDelete(rec.id)}
                      >
                        <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
                          <path d="M2 4h12M6 4V2h4v2M5 4l.5 9h5l.5-9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Delete
                      </button>

                      {user?.plan === 'pro' && (() => {
                        const status = certStatuses[rec.id];
                        return (
                          <button
                            onClick={() => navigate(`/recording/${rec.id}/certificate`)}
                            className={`rec-btn rec-btn--cert${status === 'pending' ? ' rec-btn--cert-pending' : ''}`}
                            title="Blockchain Tamper-Proof Certificate"
                          >
                            <BlockchainLockIcon size={13} />
                            {status === 'pending'
                              ? 'Verification in Progress'
                              : status === 'confirmed'
                              ? 'Blockchain Verified'
                              : 'Verify on Blockchain'}
                          </button>
                        );
                      })()}
                    </div>

                    {transcribingId === rec.id && (
                      <div className="transcript-processing">
                        <div className="processing-pulse" />
                        <div className="processing-lines">
                          <div className="processing-line processing-line--long" />
                          <div className="processing-line processing-line--short" />
                          <div className="processing-line processing-line--med" />
                        </div>
                        <p className="processing-label">AI is transcribing your audio…</p>
                      </div>
                    )}

                    {rec.transcript && expandedTranscript === rec.id && (
                      <div className="transcript-panel">
                        <div className="transcript-header">
                          <span className="transcript-label">
                            Transcript
                            {rec.transcript_data?.speaker_count > 1 && (
                              <span className="transcript-speaker-count">
                                {rec.transcript_data.speaker_count} speakers
                              </span>
                            )}
                          </span>
                          <button onClick={() => copyTranscript(rec.transcript)} className="btn-copy">
                            Copy
                          </button>
                        </div>

                        {rec.transcript_data?.segments?.length > 0 ? (
                          <div className="transcript-segments">
                            {rec.transcript_data.segments.reduce((acc, seg, i) => {
                              if (!(seg.speaker in acc.idx)) acc.idx[seg.speaker] = acc.n++;
                              const colorNum = (acc.idx[seg.speaker] % 4) + 1;
                              acc.els.push(
                                <div key={i} className={`transcript-segment speaker-${colorNum}`}>
                                  <div className="segment-meta">
                                    <span className="segment-speaker">{seg.speaker}</span>
                                    <span className="segment-time">{seg.timestamp}</span>
                                  </div>
                                  <p className="segment-text">{seg.text}</p>
                                </div>
                              );
                              return acc;
                            }, { idx: {}, n: 0, els: [] }).els}
                          </div>
                        ) : (
                          <p className="transcript-text">{rec.transcript}</p>
                        )}
                      </div>
                    )}

                  </div>
                </motion.div>
              ))}
            </div>

            {/* Infinite scroll sentinel — invisible div observed by IntersectionObserver */}
            <div ref={sentinelRef} className="rec-scroll-sentinel" aria-hidden="true" />

            {isFetchingMore && (
              <div className="rec-load-more">
                <div className="rec-load-more__cards">
                  <RecordingCardSkeleton />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Recordings;
